/**
 * 일상 기능 도메인 — 명세서 §5.5 앨범 · §5.6 일정표 · §5.7 식단표 ·
 * §5.9 투약의뢰서 · §5.10 귀가동의서 (P2)
 *
 * 서버와 클라이언트가 같은 코드·라벨을 쓰도록 여기 한 곳에 둔다.
 * DB에는 코드만 저장하고 라벨은 화면에서 붙인다 — 문구를 고칠 때 마이그레이션이 필요 없다.
 */
// ─────────────────────────────────────────────────────────────
// 일정표 (§5.6)
// ─────────────────────────────────────────────────────────────
export const EVENT_CATEGORIES = {
    FESTIVAL: 'FESTIVAL',
    FIELD_TRIP: 'FIELD_TRIP',
    BREAK: 'BREAK',
    SUPPLIES: 'SUPPLIES',
    ETC: 'ETC',
};
export const EVENT_CATEGORY_META = {
    FESTIVAL: { label: '행사', tone: 'peach' },
    FIELD_TRIP: { label: '견학', tone: 'sprout' },
    BREAK: { label: '방학·휴원', tone: 'info' },
    SUPPLIES: { label: '준비물', tone: 'peach' },
    ETC: { label: '기타', tone: 'muted' },
};
export const EVENT_CATEGORY_CODES = Object.keys(EVENT_CATEGORIES);
/** 준비물 리마인드는 전날 보낸다 (§5.6) */
export const EVENT_REMIND_DAYS_BEFORE = 1;
// ─────────────────────────────────────────────────────────────
// 식단표 (§5.7)
// ─────────────────────────────────────────────────────────────
export const MEAL_SLOTS = {
    SNACK_AM: 'SNACK_AM',
    LUNCH: 'LUNCH',
    SNACK_PM: 'SNACK_PM',
};
/** 화면에 놓이는 순서 그대로 둔다 — 오전간식 → 점심 → 오후간식 */
export const MEAL_SLOT_ORDER = ['SNACK_AM', 'LUNCH', 'SNACK_PM'];
export const MEAL_SLOT_LABELS = {
    SNACK_AM: '오전간식',
    LUNCH: '점심',
    SNACK_PM: '오후간식',
};
/**
 * 알레르기 유발물질 — 식품위생법 시행규칙 표시 대상.
 * 원아의 `allergies`와 식단의 `allergens`를 **같은 코드 체계**로 맞춰야 교차 검사가 된다.
 * 목록에 없는 값도 자유 입력으로 받되(원마다 사정이 다르다), 교차 검사는 문자열 일치로 한다.
 */
export const ALLERGENS = [
    '난류',
    '우유',
    '메밀',
    '땅콩',
    '대두',
    '밀',
    '고등어',
    '게',
    '새우',
    '돼지고기',
    '복숭아',
    '토마토',
    '아황산류',
    '호두',
    '닭고기',
    '쇠고기',
    '오징어',
    '조개류',
    '잣',
];
/**
 * 원아 알레르기와 식단 알레르겐의 교차 검사 (§5.7).
 *
 * 표기가 조금씩 달라도(공백·"알러지" 접미사) 걸리도록 정규화해서 비교한다.
 * 놓치는 쪽이 사고이므로 **부분 일치까지 잡는다** — "새우"가 "새우살"에 걸려야 한다.
 */
export function matchAllergens(childAllergies, mealAllergens) {
    const normalize = (value) => value.replace(/\s|알러지|알레르기/g, '').toLowerCase();
    const normalizedMeal = mealAllergens.map((item) => ({ raw: item, norm: normalize(item) }));
    const hits = new Set();
    for (const allergy of childAllergies) {
        const norm = normalize(allergy);
        if (!norm)
            continue;
        for (const meal of normalizedMeal) {
            if (!meal.norm)
                continue;
            if (meal.norm.includes(norm) || norm.includes(meal.norm))
                hits.add(meal.raw);
        }
    }
    return [...hits];
}
// ─────────────────────────────────────────────────────────────
// 투약의뢰서 (§5.9)
// ─────────────────────────────────────────────────────────────
export const MEDICATION_STATUS = {
    REQUESTED: 'REQUESTED',
    ADMINISTERED: 'ADMINISTERED',
    CANCELED: 'CANCELED',
};
export const MEDICATION_STATUS_LABELS = {
    REQUESTED: '투약 대기',
    ADMINISTERED: '투약 완료',
    CANCELED: '취소',
};
export const MEDICINE_TYPES = ['물약', '가루약', '알약', '연고', '기타'];
export const MEDICATION_STORAGE_OPTIONS = ['실온', '냉장'];
/** 투약 시간은 HH:mm 로만 받는다 — 서버·클라이언트가 같은 규칙을 쓴다. */
export const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
export function isTimeOfDay(value) {
    return TIME_OF_DAY_PATTERN.test(value);
}
// ─────────────────────────────────────────────────────────────
// 귀가동의서 (§5.10)
// ─────────────────────────────────────────────────────────────
export const PICKUP_REQUEST_STATUS = {
    REQUESTED: 'REQUESTED',
    CONFIRMED: 'CONFIRMED',
    REJECTED: 'REJECTED',
};
export const PICKUP_REQUEST_STATUS_LABELS = {
    REQUESTED: '확인 대기',
    CONFIRMED: '확인 완료',
    REJECTED: '반려',
};
/** 하원 담당자 관계 — 자유 입력도 허용하되 흔한 값은 골라 쓰게 한다. */
export const PICKUP_RELATIONS = [
    '어머니',
    '아버지',
    '할머니',
    '할아버지',
    '이모',
    '고모',
    '삼촌',
    '외삼촌',
    '기타',
];
// ─────────────────────────────────────────────────────────────
// 앨범 (§5.5)
// ─────────────────────────────────────────────────────────────
/** 한 앨범에 담는 사진 수 상한 — 업로드 화면과 서버가 같은 값을 본다. */
export const ALBUM_MAX_PHOTOS = 60;
/**
 * 앨범 영상 (§5.5 v1.22) — 브라우저 <video>가 바로 재생하는 형식만 받는다.
 * 사진·문서 상한(20MB)과 별개로 영상은 200MB까지. 업로드 화면과 MediaService가 같은 값을 본다.
 */
export const ALBUM_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
export const MEDIA_MAX_BYTES = 20 * 1024 * 1024;
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
export function isVideoMime(mimeType) {
    return Boolean(mimeType && mimeType.startsWith('video/'));
}
//# sourceMappingURL=daily-life.js.map